
import { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { callActions } from "../../../../core/data/redux/live-call/slice";

const AutoCallNavBlocker = () => {
    const call = useSelector((s: any) => s.liveCall);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const isAutoCallRef = useRef(call.isAutoCall);
    const activeCallRef = useRef(call.activeCall);
    const pendingPathRef = useRef<string | null>(null);
    const isConfirmingRef = useRef(false);

    useEffect(() => { isAutoCallRef.current = call.isAutoCall; }, [call.isAutoCall]);
    useEffect(() => { activeCallRef.current = call.activeCall; }, [call.activeCall]);

    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (!isAutoCallRef.current || !activeCallRef.current) return;
            if (isConfirmingRef.current) return;

            let el = e.target as HTMLElement | null;
            while (el) {
                if (el.tagName === "A") {
                    const href = (el as HTMLAnchorElement).getAttribute("href");
                    if (
                        href &&
                        !href.startsWith("#") &&
                        !href.startsWith("javascript") &&
                        !href.startsWith("http") &&
                        !href.startsWith("//") &&
                        href !== window.location.pathname
                    ) {
                        e.preventDefault();
                        e.stopPropagation();
                        e.stopImmediatePropagation();
                        pendingPathRef.current = href;
                        dispatch(callActions.setPendingNavigationPath(href));
                        return;
                    }
                    break;
                }
                el = el.parentElement;
            }
        };

        document.addEventListener("click", handleClick, true);
        return () => document.removeEventListener("click", handleClick, true);
    }, []);

    const confirmLeave = () => {
        const path = pendingPathRef.current;
        dispatch(callActions.setIsAutoCall(false));
        dispatch(callActions.setAutoCallStopped(true));
        dispatch(callActions.setPendingNavigationPath(null));
        pendingPathRef.current = null;

        if (path) {
            isConfirmingRef.current = true;
            navigate(path);
            setTimeout(() => { isConfirmingRef.current = false; }, 200);
        }
    };

    const cancelLeave = () => {
        pendingPathRef.current = null;
        dispatch(callActions.setPendingNavigationPath(null));
    };

    if (!call.pendingNavigationPath) return null;

    return (
        <>
            {/* Backdrop — swallows all pointer events */}
            <div
                onClickCapture={(e) => { e.stopPropagation(); e.preventDefault(); }}
                style={{
                    position: "fixed", inset: 0,
                    background: "rgba(0,0,0,0.45)",
                    zIndex: 99998,
                    backdropFilter: "blur(2px)",
                    cursor: "not-allowed",
                }}
            />

            {/* Modal */}
            <div style={{
                position: "fixed", top: "50%", left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 99999, background: "#fff",
                borderRadius: 16, padding: "32px 36px",
                width: 420, boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
                textAlign: "center",
            }}>
                <div style={{
                    width: 56, height: 56, borderRadius: "50%",
                    background: "#fff3cd",
                    display: "flex", alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 20px", fontSize: 26,
                }}>
                    ⚠️
                </div>

                <h5 style={{ fontWeight: 700, marginBottom: 8, color: "#111827" }}>
                    Auto Call in Progress
                </h5>

                <p style={{ color: "#6b7280", fontSize: 14, marginBottom: 24, lineHeight: 1.6 }}>
                    {call.activeCall?.name && (
                        <>
                            <strong style={{ color: "#111827" }}>
                                Currently calling: {call.activeCall.name}
                            </strong>
                            <br />
                        </>
                    )}
                    If you leave now, <strong>auto call will stop</strong>.<br />
                    The current call will continue in the background.
                </p>

                <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
                    <button
                        onClick={cancelLeave}
                        style={{
                            padding: "10px 24px", borderRadius: 8,
                            border: "1.5px solid #d1d5db",
                            background: "#fff", color: "#374151",
                            fontWeight: 600, cursor: "pointer", fontSize: 14,
                        }}
                    >
                        Stay Here
                    </button>
                    <button
                        onClick={confirmLeave}
                        style={{
                            padding: "10px 24px", borderRadius: 8,
                            border: "none", background: "#ef4444",
                            color: "#fff", fontWeight: 600,
                            cursor: "pointer", fontSize: 14,
                        }}
                    >
                        Leave & Stop Auto Call
                    </button>
                </div>
            </div>
        </>
    );
};

export default AutoCallNavBlocker;
